// Keeps the demo seed's dates fresh however long ago it was written.
//
// The seed is authored once and then read for months. Left alone, a visitor
// arriving in December sees a news feed dated September and a "next day
// forecast" from a quarter ago, which is the fastest way to make a live demo
// look abandoned.
//
// scripts/seed-demo.js records the day it ran as appMeta/seedMeta.anchorDate,
// and everything dated in the seed is written relative to that day. Shifting
// every dated field by (today - anchor) at clone time puts it back where the
// author meant it: a post written "2 days ago" is 2 days old forever.
//
// Only the fields listed in DATE_FIELDS are touched. Anything not named — hire
// dates, birthdays, survey round ids — is left exactly as seeded, because it
// means a real calendar date rather than a relative one.
//
// Pure functions with no Firestore imports, so they can be unit-tested:
//     node scripts/test-retime.mjs

const DATE_FIELDS = {
  news: ["date"],
  events: ["date"],
  conversations: ["createdAt", "lastMessageAt", "lastMessage.sentAt"],
  "conversations/messages": ["sentAt"],
  dailyReports: ["date", "createdAt", "updatedAt", "finalisedAt", "sectionMeta.*.at"],
  "dailyReports/incidents": ["createdAt", "updatedAt"],
  contactRequests: ["requestDate", "responses[].date"],
  userRequests: ["date"],
  learningVideos: ["createdAt"],
  videoWatches: ["lastUpdated"],
  vacancies: ["createdAt"],
  "vacancies/referrals": ["createdAt"],
  loginPopups: ["createdAt"],
  surveyRoundComments: ["createdAt"],
  shAssignments: ["createdAt"],
  shMaintenance: ["createdAt", "resolvedAt"],
  userAccessRequests: ["submittedAt"],
  smsHistory: ["sentAt", "createdAt"],
  pushNotificationHistory: ["sentAt"],
  adminActivityLogs: ["timestamp"],
  birthdaySmsLog: ["date", "processedAt"],
  anniversarySmsLog: ["date", "processedAt"],
  allowedAccessBackups: ["createdAt"],
  pendingProfiles: ["createdAt"],
  // Staff profiles are cloned separately (cloneProfiles), but go through the
  // same retiming: an approval that falls out of the visitor's current month
  // reads as "not approved" on the Working Hours tab.
  profiles: ["workingHoursApprovedAt", "lastSeen"],
};

// Collections whose DOCUMENT ID is a calendar day. Their ids have to move by
// the same offset as their contents, or the page that looks a day up by id
// finds nothing.
const DATE_KEYED_COLLECTIONS = ["dailyReports", "birthdaySmsLog", "anniversarySmsLog"];

const DAY_MS = 86400000;
const isYmd = (v) => typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v);

function localYmd(d) {
  const p = (x) => String(x).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

// Shifts one value, preserving whatever shape it arrived in: a Firestore
// Timestamp stays a Date, an ISO string stays an ISO string, and a bare
// YYYY-MM-DD stays a bare YYYY-MM-DD.
function shiftValue(v, offsetMs) {
  if (v == null) return v;
  if (typeof v?.toDate === "function") return new Date(v.toDate().getTime() + offsetMs);
  if (v instanceof Date) return new Date(v.getTime() + offsetMs);
  if (isYmd(v)) return localYmd(new Date(new Date(v + "T12:00:00").getTime() + offsetMs));
  if (typeof v === "string") {
    const t = Date.parse(v);
    return Number.isNaN(t) ? v : new Date(t + offsetMs).toISOString();
  }
  return v;
}

// Applies one dotted path, supporting a "*" segment (every key of a map) and a
// "[]" suffix (every element of an array).
function shiftPath(obj, path, offsetMs) {
  const [head, ...rest] = path.split(".");
  if (head === "*") {
    Object.keys(obj || {}).forEach((k) => {
      if (obj[k] && typeof obj[k] === "object") shiftPath(obj[k], rest.join("."), offsetMs);
    });
    return;
  }
  const isArray = head.endsWith("[]");
  const key = isArray ? head.slice(0, -2) : head;
  if (!(key in (obj || {}))) return;
  if (isArray) {
    if (!Array.isArray(obj[key])) return;
    obj[key] = obj[key].map((el) => {
      const copy = { ...el };
      if (rest.length) shiftPath(copy, rest.join("."), offsetMs);
      return copy;
    });
    return;
  }
  if (rest.length === 0) {
    obj[key] = shiftValue(obj[key], offsetMs);
    return;
  }
  if (obj[key] && typeof obj[key] === "object") {
    obj[key] = { ...obj[key] };
    shiftPath(obj[key], rest.join("."), offsetMs);
  }
}

function retimeDoc(collectionKey, data, offsetMs) {
  const fields = DATE_FIELDS[collectionKey];
  if (!fields || offsetMs === 0) return data;
  const out = { ...data };
  fields.forEach((f) => shiftPath(out, f, offsetMs));
  return out;
}

export {
  DATE_FIELDS,
  DATE_KEYED_COLLECTIONS,
  isYmd,
  localYmd,
  shiftValue,
  shiftPath,
  retimeDoc,
};
